import { useCallback } from 'react';

import { useNavigate } from '@suntex.core/nav';

import { useNavigationContext } from './NavigationContext';

/**
 * Returns true if the given href can be navigated to via React Router
 * (i.e. it has a built-in module route and doesn't require a full page reload).
 *
 * Strips `/preview/` from the path before checking, so preview variants
 * (e.g. `/preview/modules/gym/...`) are also recognized.
 */
function canRouterNavigate(href: string, prefixes: readonly string[]): boolean {
  const normalized = href.replace('/preview/', '/');
  return prefixes.some((prefix) => normalized.startsWith(prefix));
}

/**
 * React hook that returns a `navigateToModule` function.
 *
 * Uses React Router for hrefs whose path prefix is listed in the
 * `NavigationContext` (populated via `NavigationProvider`), and falls back to
 * a full page reload for all other hrefs.
 *
 * When no `NavigationProvider` is present the default prefixes apply.
 * Pass `routerPrefixes={[]}` via `NavigationProvider` to force all navigation
 * to use `window.location.href` (e.g. in the classic environment).
 */
export function useModuleNavigation(): {
  navigateToModule: (href: string) => void;
} {
  const reactNavigate = useNavigate();
  const { routerPrefixes } = useNavigationContext();
  const navigateToModule = useCallback(
    (href: string) => {
      if (canRouterNavigate(href, routerPrefixes)) {
        reactNavigate(href);
      } else {
        window.location.href = href;
      }
    },
    [reactNavigate, routerPrefixes],
  );
  return { navigateToModule };
}
